import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InputNumber, { InputNumberProps } from 'antd/lib/input-number';
import { useAppContext } from 'src/app/state';
import { IoMdClose } from 'react-icons/io';
import { AppRoutes } from 'src/types/app-routes.enum';
import styles from './VyduraReminderPage.module.css';
import Button from 'src/app/components/kit/button/Button';
import Text from 'src/app/components/kit/text/Text';
import Dropdown from 'src/app/components/kit/dropdown/Dropdown';
import DateTimePicker from 'src/app/components/kit/date-time-picker/DateTimePicker';
import TimePicker from 'src/app/components/kit/time-picker/TimePicker';
import {
  getDateRange,
  getPreviousDate,
  isDateInRange,
} from 'src/app/utils/calendar.utils';
import { TimeOptions } from 'src/types/date-time.types';
import { ActionType } from 'src/app/state/action-type.enum';
import { isBefore } from 'date-fns';
import { VyduraSchedule } from 'src/types/vydura-reminder';
import ActionButton from 'src/app/components/kit/action-button/ActionButton';
import AnimatedContainer from 'src/app/components/kit/animated-container/AnimatedContainer';

const ResetButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      type='delete'
      width='full-width'
      onClick={onClick}
      style={{ width: '100%', marginLeft: 0 }}
    >
      Reset
    </Button>
  );
};

const VyduraReminderPage = () => {
  const navigate = useNavigate();
  const {
    state: { vyduraReminder, vyduraSchedule, migraineAttacks },
    dispatch,
  } = useAppContext();
  const [reminder, setReminder] = useState(
    vyduraReminder ?? {
      time: undefined,
      pills: undefined,
    }
  );
  const [schedule, setSchedule] = useState(
    vyduraSchedule ?? {
      prescribtionDate: getPreviousDate(new Date()),
      startDate: undefined,
      endDate: undefined,
      frequency: 'Every other day',
      isActive: false,
    }
  );
  const [hasPrevention, setHasPrevention] = useState<Boolean | undefined>(
    vyduraSchedule?.isActive || undefined
  );
  const [hasReminder, setHasReminder] = useState<Boolean | undefined>(
    Boolean(vyduraReminder?.time) || undefined
  );
  const handleWindowClose = () => navigate(AppRoutes.Home);
  const onSelectStartDate = (answer: Date) =>
    setSchedule((prev) => ({
      ...prev,
      startDate: answer,
    }));
  const onSelectEndDate = (answer: Date) =>
    setSchedule((prev) => ({
      ...prev,
      endDate: answer,
    }));
  const onSelectReminderTime = (time: TimeOptions) =>
    setReminder((prev) => ({
      ...prev,
      time,
    }));
  const onSelectPillsNumber: InputNumberProps['onChange'] = (value) =>
    setReminder((prev) => ({
      ...prev,
      pills: value as number,
    }));
  const onSaveData = () => {
    dispatch({
      type: ActionType.SET_VYDURA_REMINDER,
      payload: {
        vyduraReminder:
          hasReminder && hasPrevention
            ? reminder
            : { pills: vyduraReminder?.pills },
      },
    });

    dispatch({
      type: ActionType.SET_VYDURA_SCHEDULE,
      payload: {
        vyduraSchedule: hasPrevention
          ? { ...schedule, isActive: true }
          : ({
              prescribtionDate: vyduraSchedule?.prescribtionDate,
              startDate: vyduraSchedule?.startDate,
              frequency: vyduraSchedule?.frequency,
              isActive: false,
            } as VyduraSchedule),
      },
    });

    const today = new Date();
    const vyduraDays = hasPrevention
      ? getDateRange(
          new Date(schedule.startDate!),
          schedule.endDate
            ? new Date(schedule.endDate)
            : new Date(new Date().getFullYear(), 11, 31)
        )
      : [];
    dispatch({
      type: ActionType.SET_MIGRAINE_ATTACK,
      payload: {
        migraineAttacks: [
          ...migraineAttacks.filter(
            (attack) =>
              isBefore(attack.date!, getPreviousDate(today)) &&
              !isDateInRange(
                attack.date!,
                vyduraDays[0],
                vyduraDays[vyduraDays.length - 1]
              )
          ),
          ...vyduraDays.map((date) => ({
            date,
            vyduraPerAttack: false,
            vyduraEveryOtherDay: true,
            isMigraineDay: false,
          })),
        ],
      },
    });
    handleWindowClose();
  };
  const onReset = () => {
    dispatch({
      type: ActionType.SET_VYDURA_REMINDER,
      payload: { vyduraReminder: { pills: vyduraReminder?.pills } },
    });
    dispatch({
      type: ActionType.SET_VYDURA_SCHEDULE,
      payload: {
        vyduraSchedule: {
          prescribtionDate: vyduraSchedule?.prescribtionDate,
          startDate: vyduraSchedule?.startDate,
          frequency: vyduraSchedule?.frequency,
          isActive: false,
        } as VyduraSchedule,
      },
    });
    dispatch({
      type: ActionType.SET_MIGRAINE_ATTACK,
      payload: {
        migraineAttacks: [
          ...migraineAttacks.filter((attack) =>
            isBefore(attack.date!, new Date())
          ),
        ],
      },
    });
    handleWindowClose();
  };

  useEffect(() => {
    if (hasPrevention === false) {
      setHasReminder(false);
    }
  }, [hasPrevention]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Button
          type='transparent'
          width='content-width'
          style={{ padding: 0, height: '21.6px' }}
          onClick={handleWindowClose}
        >
          <IoMdClose size={20} />
        </Button>
      </div>
      <div className={styles.content}>
        <div>
          <Text tag='h2' size='m-large' weight='bold'>
            Intake schedule
          </Text>
          <Text weight='bold'>You take Vydura as a preventive?</Text>
          <Button
            type={hasPrevention === true ? 'dark-grey' : 'light-grey'}
            width='full-width'
            onClick={() => setHasPrevention(true)}
            style={{ marginBottom: '10px' }}
          >
            Yes
          </Button>
          <Button
            type={hasPrevention === false ? 'dark-grey' : 'light-grey'}
            width='full-width'
            onClick={() => setHasPrevention(false)}
          >
            No
          </Button>
          <AnimatedContainer
            isVisible={Boolean(hasPrevention)}
            name='prevention'
          >
            <Text weight='bold'>Frequency</Text>
            <Dropdown
              placeholder='Every other day'
              options={[]}
              value={null}
              onChange={() => undefined}
              disabled
            />
            <Text weight='bold'>Start date</Text>
            <DateTimePicker
              showTimePicker={false}
              minDate={vyduraSchedule?.prescribtionDate}
              selectedDate={schedule.startDate}
              showFullDateWithTime={false}
              handleSelectDateTime={onSelectStartDate}
            />
            <Text weight='bold'>End date</Text>
            <DateTimePicker
              showTimePicker={false}
              minDate={schedule.startDate ?? schedule.prescribtionDate}
              selectedDate={schedule.endDate}
              showFullDateWithTime={false}
              handleSelectDateTime={onSelectEndDate}
            />
          </AnimatedContainer>
        </div>
        <div>
          <Text tag='h2' size='m-large' weight='bold'>
            Reminder settings
          </Text>
          <Text weight='bold'>Do you need a reminder?</Text>
          <Button
            type={hasReminder === true ? 'dark-grey' : 'light-grey'}
            width='full-width'
            onClick={() => setHasReminder(true)}
            style={{ marginBottom: '10px' }}
            isReadOnly={!hasPrevention}
          >
            Yes
          </Button>
          <Button
            type={hasReminder === false ? 'dark-grey' : 'light-grey'}
            width='full-width'
            onClick={() => setHasReminder(false)}
          >
            No
          </Button>
          <AnimatedContainer isVisible={Boolean(hasReminder)} name='reminder'>
            <Text weight='bold'>Reminder time</Text>
            <TimePicker
              selectedTime={reminder.time}
              handleSelectTime={onSelectReminderTime}
            />
            <Text weight='bold'>Remaining pills</Text>
            <InputNumber
              size='large'
              placeholder='E.g. 18'
              min={0}
              max={99}
              value={reminder.pills}
              onChange={onSelectPillsNumber}
            />
            <Text weight='bold'>You will receive 1 reminder</Text>
            <ul>
              <li>When 5 pills left</li>
            </ul>
          </AnimatedContainer>
        </div>
      </div>
      <div className={styles.footer}>
        {vyduraSchedule?.isActive ? (
          <>
            <div className={styles.flex1}>
              <ActionButton
                onConfirm={onReset}
                MainButton={ResetButton}
                popUpText={
                  <Text color='gray'>
                    This will clear your intake schedule and remove future
                    reminders for Vydura 75mg every other day. Your past records
                    will stay unchanged.
                  </Text>
                }
                popUpHeader='Reset schedule & reminders'
                hasFooter={true}
                skipButtonText='Cancel'
                confirmButtonText='Reset'
              />
            </div>
            <div className={styles.flex1}>
              <Button
                type='primary'
                width='full-width'
                style={{ width: '100%', marginLeft: 0 }}
                onClick={onSaveData}
              >
                Save
              </Button>
            </div>
          </>
        ) : (
          <Button
            type='primary'
            width='full-width'
            style={{ width: '100%', marginLeft: 0 }}
            onClick={onSaveData}
            isReadOnly={
              hasPrevention === undefined || (!hasPrevention && hasReminder)
            }
          >
            Add schedule
          </Button>
        )}
      </div>
    </div>
  );
};

export default VyduraReminderPage;
